/**
 * پرداختِ حقوقِ دوره — «چقدر باید داد، چقدر داده شد، چقدر مانده».
 *
 * تا پیش از این هیچ‌جا ثبت نمی‌شد که پولِ چه کسی واقعاً پرداخت شده؛ «لیست پرداخت
 * حقوق» فقط یک لیستِ *باید‌پرداخت* بود و بعدش رد پایی نمی‌ماند.
 *
 * ⚠️ پرداختی که اینجا ثبت می‌شود عمداً وارد **کسوراتِ** محاسبهٔ حقوق نمی‌شود؛
 * وگرنه «محاسبهٔ مجدد» همان ماه دوباره از ناخالص کمش می‌کرد و حقوق نصف می‌شد.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Dropdown, Empty, Form, Grid, Modal, Pagination, Popconfirm, Row, Segmented, Select, Table, Space, Statistic, Tag, Tooltip, Typography, message } from "antd";
import Alert from "../../components/GxAlert";
import { CheckOutlined, DeleteOutlined, DollarOutlined, DownOutlined, EditOutlined, FileExcelOutlined, LockOutlined, ReloadOutlined, SwapOutlined, UnlockOutlined } from "@ant-design/icons";
import TransferModal from "../../components/TransferModal";
import GxTable from "../../components/GxTable";
import CopyText from "../../components/CopyText";
import { MoneyInput, formatMoney } from "../../utils/money";
import { JalaliDateInput, toJalali, todayISO } from "../../utils/dateUtils";
import { byNum, byStr } from "../../utils/sorters";
import { apiError } from "../../utils/apiError";
import {
  getPeriodSalaryPayments, payPeriodSalariesBulk, postPeriodToLedger,
  recordSalaryPayment, downloadPeriodPaymentFile,
  getSalaryPaymentItems, updateSalaryPayment, deleteSalaryPayment, closePeriodPayments, reopenPeriodPayments,
} from "../../api/client";
import AdminPinModal from "../../components/AdminPinModal";
import confirmEntry from "../../utils/entryCheck";
import SourceSelect, { OFFICE } from "../../components/SourceSelect";
import useRefreshOnShow from "../../utils/useRefreshOnShow";

const { Text } = Typography;

const STATUS = {
  unpaid: { label: "پرداخت نشده", color: "red" },
  partial: { label: "نیمه‌پرداخت", color: "orange" },
  paid: { label: "پرداخت شد", color: "green" },
  negative: { label: "حقوق منفی", color: "purple" },
  nothing: { label: "بدون مبلغ", color: "default" },     // v2.166.2: خالصِ صفر — «پرداخت نشد» نیست
};

const METHODS = [
  { value: "bank", label: "بانکی" }, { value: "cash", label: "نقدی" },
  { value: "check", label: "چک" }, { value: "other", label: "سایر" },
];

/** وضعیت با توضیحِ انتقال: «تسویه شد (با انتقال)» / «بدهکار» به‌جای «حقوق منفی» وقتی منفی از انتقال است. */
function statusLabel(r) {
  const moved = (r.transfer_in || 0) + (r.transfer_out || 0) > 0;
  if (r.status === "paid") return moved && !r.paid ? "تسویه شد (با انتقال)" : moved ? "تسویه شد" : STATUS.paid.label;
  if (r.status === "negative") return r.payable < 0 ? (moved ? "حقوق منفی — بخشی صاف شد" : STATUS.negative.label) : "بدهکار";
  return STATUS[r.status]?.label || r.status;
}

function RemainingCell({ v, r }) {
  if (v > 0) {
    return <CopyText copy={String(Math.round(v))}><b style={{ color: "#C0392B" }}>{formatMoney(v)}</b></CopyText>;
  }
  if (v < 0) return <b style={{ color: "#8e44ad" }}>{formatMoney(-v)} بدهکار</b>;
  const moved = (r.transfer_in || 0) + (r.transfer_out || 0) > 0;
  return moved || r.paid ? <Tag color="green" style={{ margin: 0 }}>✓ تسویه</Tag> : <Text type="secondary">۰</Text>;
}

/** سطرهای انتقال — هر انتقال یک سطرِ کوتاه («⬅ از زهرا مرادی: X»). نتیجه فقط در «مانده/وضعیت» است
    (کاربر: «دوبار نوشته» — v2.136.2 جملهٔ نتیجهٔ تکراری حذف شد). کلیک ⇒ ویرایش. */
function TransferLines({ r, onEdit }) {
  const short = (t) => (t || "").replace("انتقال به حسابِ ", "به ").replace("از حسابِ ", "از ");
  return (
    <div style={{ fontSize: 12, lineHeight: 1.8 }}>
      {r.transfers.map((x, i) => (
        <div key={i}>
          <a onClick={onEdit && x.in_id ? () => onEdit(x.in_id) : undefined}
            style={{ color: x.dir === "in" ? "#107C41" : "#C0392B", cursor: onEdit && x.in_id ? "pointer" : "default" }}
            title={onEdit && x.in_id ? "ویرایشِ این انتقال" : undefined}>
            {x.dir === "in" ? "⬅ " : "➡ "}{short(x.text)}: <b>{formatMoney(x.amount)}</b>
          </a>
        </div>
      ))}
    </div>
  );
}

export default function SalaryPaymentsTab({ periodId, periodSelector, canEdit, focus, active = true }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [payFor, setPayFor] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [transfer, setTransfer] = useState(null);   // v2.133.0: انتقال بین پرسنل
  const openEdit = (inId) => setTransfer({ edit_in_id: inId });
  // v2.137.0 — پرداخت‌های هر نفر (ویرایش/حذف) و بستنِ پرونده
  const [itemsFor, setItemsFor] = useState(null);     // ردیفِ نفر
  const [items, setItems] = useState([]);
  const [editItem, setEditItem] = useState(null);
  const [editForm] = Form.useForm();
  const [pinOpen, setPinOpen] = useState(false);
  const closedAt = data?.payments_closed_at;
  const closed = !!closedAt;
  const edit = canEdit && !closed;
  const loadItems = (r) => getSalaryPaymentItems(periodId, r.employee_id)
    .then((res) => { setItems(res.data || []); return res.data || []; }).catch(() => { setItems([]); return []; });
  const startItemEdit = (it) => {
    setEditItem(it);
    editForm.setFieldsValue({ amount: it.amount, paid_at: it.paid_at, method: it.method,
      reference_number: it.reference_number, note: it.note,
      source: { source_account: it.source_account || "office", petty_account_id: it.petty_account_id } });
  };
  const openItems = (r, paymentId) => {
    setItemsFor(r); setEditItem(null);
    loadItems(r).then((list) => {
      const it = paymentId && list.find((x) => x.id === paymentId);
      if (it && it.editable && canEdit && !closed) startItemEdit(it);
    });
  };
  /* v2.140.0 — از «اسناد مالی»/«دفتر حساب» روی سندِ پرداختِ حقوق ⇒ همین‌جا همان نفر و همان پرداخت باز شود. */
  const [focusDone, setFocusDone] = useState(null);
  useEffect(() => {
    if (!focus || focusDone === focus.at || !data || data.period_id !== periodId) return;
    const r = (data.rows || []).find((x) => x.employee_id === focus.employee_id);
    setFocusDone(focus.at);
    if (r) openItems(r, focus.payment_id);
    else message.info("این نفر در لیستِ پرداختِ این ماه نیست");
  }, [focus, data, periodId]);
  const [form] = Form.useForm();
  const [bulkForm] = Form.useForm();

  const load = useCallback(() => {
    if (!periodId) { setData(null); return; }
    setLoading(true);
    getPeriodSalaryPayments(periodId)
      .then((r) => setData(r.data))
      .catch(() => message.error("خطا در دریافت لیست پرداخت"))
      .finally(() => setLoading(false));
  }, [periodId]);

  useEffect(() => { load(); }, [load]);
  useRefreshOnShow(load, active);     // v2.167.0: برگشت به تب/پنجره ⇒ تازه

  const doPost = () => postPeriodToLedger(periodId).then((r) => {
    const d = r.data;
    if (d.skipped_reason) message.warning(d.skipped_reason);
    else message.success(`${d.posted} طلب و ${d.debts} بدهی در دفتر ثبت شد`);
    load();
  }).catch((e) => message.error(e?.response?.data?.detail || "ثبت در دفتر ناموفق بود"));

  const askClose = () => {
    const open = (data?.rows || []).filter((r) => r.remaining > 0);
    const neg = (data?.rows || []).filter((r) => r.remaining < 0);
    Modal.confirm({
      title: "پروندهٔ پرداختِ این ماه بسته شود؟",
      icon: <LockOutlined />,
      width: 520,
      content: (
        <div style={{ lineHeight: 2 }}>
          {open.length
            ? <div>• <b>{formatMoney(open.length)} نفر</b> هنوز <b>{formatMoney(open.reduce((a, r) => a + r.remaining, 0))}</b> ریال
                پرداخت‌نشده دارند. این مبلغ <b>پاک نمی‌شود</b>؛ در «مانده و وضعیت حساب» به‌عنوانِ طلبِ همان نفر می‌ماند
                و ماه‌های بعد دیده می‌شود.</div>
            : <div>• همه پرداخت شده‌اند.</div>}
          {neg.length > 0 && <div>• {formatMoney(neg.length)} نفر بدهکار مانده‌اند؛ بدهی‌شان هم در حسابشان می‌ماند.</div>}
          <div>• بعد از بستن، پرداخت/ویرایش/انتقالِ این ماه ممکن نیست مگر با <b>کدِ مدیر</b> باز شود.</div>
        </div>),
      okText: "ببند", cancelText: "انصراف",
      onOk: () => closePeriodPayments(periodId).then((r) => { message.success(r.data?.message || "بسته شد"); load(); })
        .catch((e) => message.error(apiError(e))),
    });
  };

  const cols = [
    { title: "پرسنل", dataIndex: "employee_name", width: 180, sorter: byStr("employee_name") },
    { title: "کد", dataIndex: "employee_code", width: 80 },
    // v2.132.0: «بیمه‌ای/غیربیمه‌ای» و «سمت» — قابلِ مرتب‌سازی
    { title: "بیمه", dataIndex: "insured_label", width: 100,
      filters: [{ value: "بیمه‌ای", text: "بیمه‌ای" }, { value: "غیربیمه‌ای", text: "غیربیمه‌ای" }],
      onFilter: (v, r) => r.insured_label === v,
      render: (v) => <Tag color={v === "بیمه‌ای" ? "blue" : "default"}>{v}</Tag> },
    { title: "سمت", dataIndex: "job_title", width: 120, render: (v) => v || "—" },
    // v2.152.10: «ثابت‌ها (هرکس کارمزدی نیست) جدا — زودتر پرداخت می‌شوند»
    { title: "نوع حقوق", dataIndex: "pay_group", width: 120,
      filters: [{ value: "fixed", text: "ثابت / غیرکارمزدی" }, { value: "piece", text: "کارمزدی" }],
      onFilter: (v, r) => r.pay_group === v,
      sorter: (a, b) => String(a.pay_group).localeCompare(String(b.pay_group)),
      render: (v, r) => (<span>{v === "piece" ? <Tag color="geekblue">کارمزدی</Tag> : <Tag color="green">ثابت</Tag>}
        {r.payroll_closed && <LockOutlined title="حقوقِ این گروه بسته و نهایی است" style={{ color: "#107C41" }} />}</span>) },
    /* صاحبِ حساب کنارِ نامِ پرسنل — خواستهٔ کاربر: «چون ممکنه حساب‌هاشون
       متفاوت باشه، صاحب حسابشون هم بنویس». مقدار **زنده** از پروندهٔ پرسنل
       می‌آید، پس اگر آنجا عوض شود اینجا هم عوض می‌شود. */
    { title: "صاحب حساب", dataIndex: "account_holder", width: 160,
      sorter: byStr("account_holder"),
      render: (v, r) => (v && v !== r.employee_name
        ? <b>{v}</b>
        : <Text type="secondary">{v || "خودِ شخص"}</Text>) },
    {
      title: "وضعیت", dataIndex: "status", width: 110,
      filters: Object.entries(STATUS).map(([v, s]) => ({ value: v, text: s.label })),
      onFilter: (v, r) => r.status === v,
      render: (v, r) => <Tag color={STATUS[v].color}>{statusLabel(r)}</Tag>,
    },
    { title: "قابلِ پرداخت (ریال)", dataIndex: "payable", width: 150, align: "left",
      sorter: byNum("payable"), render: (v) => formatMoney(v) },
    { title: "پرداخت‌شده", dataIndex: "paid", width: 150, align: "left",
      sorter: byNum("paid"),
      // کلیک ⇒ فهرستِ پرداخت‌های همین نفر با ویرایش/حذف (کاربر: «شاید یک پرداختیِ اشتباه زدیم»)
      render: (v, r) => (v ? <a onClick={() => openItems(r)} title="دیدن / ویرایشِ پرداخت‌ها">
        <b style={{ color: "#107C41" }}>{formatMoney(v)}</b> <EditOutlined style={{ fontSize: 11 }} /></a>
        : <Text type="secondary">—</Text>) },
    /* v2.135.0 — انتقال بین پرسنل در همین لیست (کاربر: «باید در لیستِ حقوق هم باشد با توضیح»).
       کلیک روی هر سطر ⇒ ویرایشِ همان انتقال. */
    { title: "انتقال", dataIndex: "transfers", width: 260,
      sorter: (a, b) => (a.transfer_in - a.transfer_out) - (b.transfer_in - b.transfer_out),
      render: (v, r) => (v && v.length ? <TransferLines r={r} onEdit={edit ? openEdit : null} />
        : <Text type="secondary">—</Text>) },
    { title: "مانده", dataIndex: "remaining", width: 170, align: "left",
      sorter: byNum("remaining"),
      // کلیک یا آیکون ⇒ مبلغِ خام (بدونِ جداکننده) در کلیپ‌بورد
      render: (v, r) => <RemainingCell v={v} r={r} /> },
    { title: "آخرین پرداخت", dataIndex: "last_paid_at", width: 120,
      render: (v) => (v ? toJalali(v) : <Text type="secondary">—</Text>) },
    { title: "روش", dataIndex: "payment_method", width: 90,
      render: (v) => (v === "insurance" ? "بیمه — بانکِ رسمی"
        : (METHODS.find((m) => m.value === v)?.label || v || "—")) },
    /* از کجا پرداخت شده — دفتر یا تنخواه (خواستهٔ کاربر: «مشخص باشه») */
    { title: "مبدأ", dataIndex: "sources", width: 110,
      render: (v) => (!v || !v.length ? "—" : v.map((x) => (
        <Tag key={x} color={x === "petty" ? "geekblue" : "default"}>{x === "petty" ? "تنخواه" : "دفتر"}</Tag>))) },
    { title: "شبا", dataIndex: "iban", width: 250,
      render: (v) => <CopyText copy={v} mono>{v}</CopyText> },
    { title: "شماره کارت", dataIndex: "card_number", width: 190,
      render: (v) => <CopyText copy={v} mono>{v}</CopyText> },
    ...(canEdit ? [{
      title: "", width: 190,
      render: (_, r) => (
        <Space size={4}>
          {/* v2.138.0 — کاربر: «بخش پرداخت حقوق ویرایش ندارد». دکمهٔ روشن جلوی هر ردیف (قبلاً فقط کلیک روی مبلغ). */}
          {r.paid > 0 && (
            <Button size="small" icon={<EditOutlined />} onClick={() => openItems(r)}
              title="دیدن / ویرایش / حذفِ پرداخت‌های این نفر">ویرایش</Button>
          )}
          {edit && r.remaining > 0 && (
            <Button size="small" type="primary" icon={<CheckOutlined />}
              onClick={() => {
                setPayFor(r);
                form.setFieldsValue({ amount: r.remaining, paid_at: todayISO(), method: "bank", source: OFFICE });
              }}>پرداخت</Button>
          )}
          {/* حقوقِ این نفر را به حسابِ کسِ دیگری بده (چکِ مشترک / بدهیِ خانواده) */}
          {edit && <Button size="small" icon={<SwapOutlined />} title="انتقال به حسابِ همکار"
            onClick={() => setTransfer({ from_employee_id: r.employee_id,
              amount: r.remaining > 0 ? Math.round(r.remaining) : undefined, as_salary: r.remaining > 0 })} />}
        </Space>
      ),
    }] : []),
  ];

  /* انتخابِ ردیف — فایلِ پرداخت باید دقیقاً همان کسانی باشد که کاربر تیک زده،
     و اگر هیچ‌کس تیک نخورده، همهٔ کسانی که هنوز مانده دارند. */
  const [picked, setPicked] = useState([]);
  /* v2.167.0 — «دو لیست در ماه»: غیرکارمزدی (زودتر بسته و پرداخت می‌شود) و کارمزدی */
  const [group, setGroup] = useState("all");
  const groupRows = useMemo(() => (data?.rows || []).filter((r) => group === "all" || r.pay_group === group),
    [data, group]);

  const [downloading, setDownloading] = useState(false);
  // v2.125.0: سرور می‌سازد — بیمه‌ای/غیربیمه‌ای جدا، «وضعیتِ پرداخت» هر نفر، نام مثلِ «حقوق ۱۴۰۵-۰۶»
  /* v2.167.0 — `settled`: پرداخت‌شده‌ها/بی‌مانده‌ها هم با مبلغِ صفر (منفی‌ها نه)؛ `debtors`: فایلِ جدای بدهکاران */
  const makeFile = async (simple = false, bank = false, opts = {}) => {
    const src = picked.length ? groupRows.filter((r) => picked.includes(r.employee_id)) : groupRows;
    const fit = opts.debtors ? src.filter((r) => r.remaining < 0)
      : src.filter((r) => r.remaining > 0 || (opts.settled && r.remaining === 0));
    if (!fit.length) return message.warning(opts.debtors ? "بدهکاری در این انتخاب نیست" : "کسی با ماندهٔ قابلِ پرداخت انتخاب نشده");
    setDownloading(opts.debtors ? "debtors" : simple ? (bank ? "simple_bank" : "simple") : "full");
    try {
      await downloadPeriodPaymentFile(periodId, {
        employee_ids: picked.length ? fit.map((r) => r.employee_id) : null,
        simple, bank,
        include_settled: !!opts.settled,
        kind: opts.debtors ? "debtors" : "pay",
        group: group === "all" ? null : group,
      });
      message.success(opts.debtors ? "لیستِ بدهکاران ساخته شد" : simple ? "لیستِ ساده ساخته شد" : "فایلِ پرداخت ساخته شد");
    } catch (e) { message.error(apiError(e, "ساختِ فایل ناموفق بود")); }
    setDownloading(false);
  };

  // جمع‌ها: بدونِ فیلتر از سرور؛ با فیلترِ گروه، از همان ردیف‌ها
  const t = !data?.totals ? null : group === "all" ? data.totals : {
    payable: groupRows.reduce((a, r) => a + (r.payable || 0), 0),
    paid: groupRows.reduce((a, r) => a + (r.paid || 0), 0),
    remaining: groupRows.reduce((a, r) => a + Math.max(r.remaining || 0, 0), 0),
    unpaid_count: groupRows.filter((r) => r.status === "unpaid").length,
  };
  const closedGroups = data?.closed_groups || [];
  const GROUP_FA = { fixed: "غیرکارمزدی", piece: "کارمزدی" };
  // v2.132.0: «وقتی همهٔ حقوق پرداخت شد، تسویه بزند روی ماه» — مهرِ ماه بالای صفحه
  const settled = !!t && t.payable > 0 && t.remaining <= 0;
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const [mPage, setMPage] = useState(1);
  const allRows = groupRows;
  return (
    <Card
      title={<Space wrap size={6}>
        <span>پرداخت حقوق دوره</span>
        {t && (closed
          ? <Tag color="default" icon={<LockOutlined />} style={{ fontSize: 13, padding: "2px 10px" }}>
              پروندهٔ پرداخت بسته شد — {toJalali(closedAt)}</Tag>
          : settled
          ? <Tag color="green" style={{ fontSize: 13, padding: "2px 10px" }}>✅ تسویه شد — پرداختِ این ماه کامل است</Tag>
          : t.payable > 0 && <Tag color="orange">در حالِ پرداخت — {t.unpaid_count} نفر پرداخت‌نشده</Tag>)}
        {/* v2.137.0 — «بعضی‌ها را نمی‌خواهیم واریز کنیم؛ همان نصفه بماند و پرداخت تمام شود» */}
        {canEdit && t && !closed && t.payable > 0 && (
          <Button size="small" icon={<LockOutlined />} onClick={askClose}>بستنِ پرونده</Button>
        )}
        {canEdit && closed && (
          <Button size="small" icon={<UnlockOutlined />} onClick={() => setPinOpen(true)}>بازگشایی</Button>
        )}
      </Space>}
      extra={
        /* v2.134.0 — کاربر: «بالای صفحه شلوغ شده؛ همهٔ لیست‌های اکسل در یک دراپ‌داون، بازخوانی بی‌متن». */
        <Space wrap size={6}>
          {periodSelector}
          <Segmented size="middle" value={group} onChange={(v) => { setGroup(v); setPicked([]); }}
            options={[
              { value: "all", label: "همه" },
              { value: "fixed", label: <span>غیرکارمزدی{closedGroups.includes("fixed") ? " 🔒" : ""}</span> },
              { value: "piece", label: <span>کارمزدی{closedGroups.includes("piece") ? " 🔒" : ""}</span> },
            ]} />
          <Tooltip title="بازخوانی">
            <Button icon={<ReloadOutlined />} onClick={load} aria-label="بازخوانی" />
          </Tooltip>
          <Dropdown trigger={["click"]} menu={{
            items: [
              { key: "full", label: `فایلِ پرداختِ بانک (کامل)${picked.length ? ` — ${picked.length} نفر` : ""}` },
              { key: "simple", label: "لیستِ ساده (نام + مبلغ + ستونِ خالی)" },
              { key: "simple_bank", label: "لیستِ ساده با شبا و کارت" },
              { type: "divider" },
              /* v2.167.0 (کاربر): «پرداخت‌شده‌ها هم باشند ولی مبلغشان صفر؛ منفی‌ها نه» */
              { key: "full_settled", label: "فایلِ پرداخت + پرداخت‌شده‌ها با مبلغِ صفر" },
              { key: "simple_bank_settled", label: "لیستِ ساده (شبا/کارت) + پرداخت‌شده‌ها با مبلغِ صفر" },
              { key: "debtors", label: "لیستِ بدهکاران (ماندهٔ منفی) — جدا" },
              ...(canEdit ? [
                { type: "divider" },
                /* ثبت در دفتر هنگامِ «تأییدِ نهایی» خودکار انجام می‌شود و هر پرداخت هم خودش در حساب
                   می‌نشیند؛ این فقط ابزارِ ترمیم است و ردیفِ تکراری نمی‌سازد. */
                { key: "post", label: "ثبتِ دوبارهٔ حقوق در دفتر مالی (فقط اگر چیزی جا مانده)" },
              ] : []),
            ],
            onClick: ({ key }) => {
              if (key === "post") {
                Modal.confirm({
                  title: "ثبتِ دوبارهٔ حقوقِ این ماه در دفتر مالی؟",
                  content: "معمولاً لازم نیست: با «تأییدِ نهایی» حقوق خودکار در دفتر ثبت می‌شود و هر پرداخت هم خودش حساب را صاف می‌کند. فقط اگر حسابِ کسی جا افتاده، این را بزنید — ردیفِ تکراری نمی‌سازد.",
                  okText: "ثبت کن", cancelText: "انصراف", onOk: doPost,
                });
              } else if (key === "debtors") makeFile(true, false, { debtors: true });
              else if (key === "full_settled") makeFile(false, false, { settled: true });
              else if (key === "simple_bank_settled") makeFile(true, true, { settled: true });
              else makeFile(key !== "full", key === "simple_bank");
            },
          }}>
            <Button icon={<FileExcelOutlined />} loading={!!downloading}>
              فایل‌ها <DownOutlined />
            </Button>
          </Dropdown>
          {edit && (
            <Button icon={<SwapOutlined />} onClick={() => setTransfer({})}>انتقال بین پرسنل</Button>
          )}
          {edit && (
            <Button type="primary" onClick={() => {
              bulkForm.setFieldsValue({ paid_at: todayISO(), method: "bank" });
              setBulkOpen(true);
            }}>پرداختِ همه</Button>
          )}
        </Space>
      }>
      <Alert type="info" showIcon style={{ marginBottom: 12 }}
        message="پرداختی که اینجا ثبت می‌شود در محاسبهٔ حقوق کسر نمی‌شود"
        description="این ثبت فقط می‌گوید پول داده شده و ماندهٔ حسابِ او را صاف می‌کند. اگر «محاسبهٔ مجدد» بزنید، عددِ حقوق عوض نمی‌شود." />

      {t && (
        <Row gutter={12} style={{ marginBottom: 12 }}>
          <Col xs={12} md={6}><Card size="small">
            <Statistic title="جمع قابلِ پرداخت (ریال)" value={formatMoney(t.payable)} /></Card></Col>
          <Col xs={12} md={6}><Card size="small">
            <Statistic title="پرداخت‌شده" value={formatMoney(t.paid)}
              valueStyle={{ color: "#107C41" }} /></Card></Col>
          <Col xs={12} md={6}><Card size="small">
            <Statistic title="مانده" value={formatMoney(t.remaining)}
              valueStyle={{ color: "#C0392B" }} /></Card></Col>
          <Col xs={12} md={6}><Card size="small">
            <Statistic title="پرداخت‌نشده" value={t.unpaid_count} suffix="نفر" /></Card></Col>
        </Row>
      )}

      {data && data.payroll_closed === false && (data.rows || []).length > 0 && (
        closedGroups.length ? (
          /* v2.167.0 — یک لیست بسته شده، دیگری نه */
          <Alert type="success" showIcon icon={<LockOutlined />} style={{ marginBottom: 12 }}
            message={`حقوقِ ${closedGroups.map((g) => GROUP_FA[g]).join(" و ")} بسته و تأیید شد — مبالغشان نهایی است`}
            description={`لیستِ ${["fixed", "piece"].filter((g) => !closedGroups.includes(g)).map((g) => GROUP_FA[g]).join(" و ")} هنوز باز است؛ مبالغِ آن‌ها از آخرین محاسبه است و ممکن است عوض شود.`} />
        ) : (
          <Alert type="info" showIcon style={{ marginBottom: 12 }}
            message="حقوقِ این ماه هنوز بسته نشده — مبالغ از آخرین محاسبه است"
            description="می‌توانید همین حالا (مثلاً به ثابت‌ها با فیلترِ «نوع حقوق») پرداخت کنید؛ اگر محاسبه عوض شود، «باید داد» و «مانده» هم خودکار عوض می‌شوند. هر لیست را می‌شود در «محاسبه و بستن حقوق» جدا بست." />
        )
      )}
      {closed && (
        <Alert type="warning" showIcon icon={<LockOutlined />} style={{ marginBottom: 12 }}
          message={`پروندهٔ پرداختِ این ماه ${toJalali(closedAt)} بسته شد`}
          description="پرداخت، ویرایش و انتقالِ این ماه قفل است. هر مبلغی که پرداخت نشده، در «مانده و وضعیت حساب» به‌عنوانِ طلب/بدهیِ همان نفر مانده است. برای تغییر، «بازگشایی» را با کدِ مدیر بزنید." />
      )}
      {settled && !closed && (
        <Alert type="success" showIcon style={{ marginBottom: 12 }}
          message="پروندهٔ پرداختِ این ماه بسته است — همهٔ حقوق‌ها پرداخت و تسویه شد" />
      )}

      {isMobile ? (
        /* گوشی (v2.132.0): کارت به‌جای جدول — اولویت: نام، صاحبِ حساب، مبلغ، شبا (کاربر) */
        <div>
          {!allRows.length && <Empty description="ردیفی نیست" />}
          {allRows.slice((mPage - 1) * 20, mPage * 20).map((r) => (
            <Card key={r.employee_id} size="small" style={{ marginBottom: 8 }}
              styles={{ body: { padding: 10 } }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6 }}>
                <b style={{ fontSize: 15 }}>{r.employee_name}</b>
                <Space size={4}>
                  <Tag color={r.insured_label === "بیمه‌ای" ? "blue" : "default"} style={{ margin: 0 }}>{r.insured_label}</Tag>
                  <Tag color={STATUS[r.status]?.color} style={{ margin: 0 }}>{statusLabel(r)}</Tag>
                </Space>
              </div>
              <div style={{ fontSize: 12, color: "var(--gx-text-sec, #777)", margin: "2px 0 6px" }}>
                صاحب حساب: <b style={{ color: "inherit" }}>{r.account_holder || r.employee_name}</b>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "4px 8px", fontSize: 13 }}>
                <span>مانده:</span>
                {r.remaining > 0
                  ? <CopyText copy={String(Math.round(r.remaining))}><b style={{ color: "#C0392B", fontSize: 15 }}>{formatMoney(r.remaining)}</b></CopyText>
                  : <span><RemainingCell v={r.remaining} r={r} />{r.paid ? <Text type="secondary"> (پرداخت‌شده {formatMoney(r.paid)})</Text> : null}</span>}
                <span>شبا:</span><CopyText copy={r.iban} mono>{r.iban}</CopyText>
                <span>کارت:</span><CopyText copy={r.card_number} mono>{r.card_number}</CopyText>
              </div>
              {r.transfers && r.transfers.length > 0 && (
                <div style={{ marginTop: 6 }}><TransferLines r={r} onEdit={edit ? openEdit : null} /></div>
              )}
              {r.paid > 0 && (
                <Button block size="small" type="link" onClick={() => openItems(r)}>پرداخت‌ها ({formatMoney(r.paid)}) — دیدن/ویرایش</Button>
              )}
              {edit && r.remaining > 0 && (
                <Button block type="primary" icon={<CheckOutlined />} style={{ marginTop: 8 }}
                  onClick={() => {
                    setPayFor(r);
                    form.setFieldsValue({ amount: r.remaining, paid_at: todayISO(), method: "bank", source: OFFICE });
                  }}>ثبتِ پرداخت</Button>
              )}
            </Card>
          ))}
          {allRows.length > 20 && (
            <Pagination simple current={mPage} pageSize={20} total={allRows.length} onChange={setMPage}
              style={{ textAlign: "center", marginTop: 8 }} />
          )}
        </div>
      ) : (
      <GxTable layoutId="finance-salary-payments" dataSource={allRows}
        columns={cols} rowKey="employee_id" size="small" loading={loading}
        gxExportName="salary-payments"
        rowSelection={{
          selectedRowKeys: picked,
          onChange: setPicked,
          getCheckboxProps: (r) => ({ disabled: (r.remaining || 0) <= 0 }),
        }}
        pagination={{ pageSize: 30 }} scroll={{ x: "max-content" }} />
      )}

      <TransferModal open={!!transfer} defaults={transfer} periodId={periodId}
        onClose={() => setTransfer(null)} onDone={load} />

      {/* v2.137.0 — پرداخت‌های یک نفر: ویرایش / حذف */}
      <Modal open={!!itemsFor} width={720} footer={null}
        title={`پرداخت‌های ${itemsFor?.employee_name || ""}`}
        onCancel={() => { setItemsFor(null); setEditItem(null); }}>
        {closed && <Alert type="warning" showIcon style={{ marginBottom: 8 }} message="پرونده بسته است؛ فقط دیدن." />}
        <Table size="small" rowKey="id" dataSource={items} pagination={false} scroll={{ x: "max-content" }}
          columns={[
            { title: "تاریخ", dataIndex: "paid_at", render: (v) => toJalali(v) },
            { title: "مبلغ", dataIndex: "amount", render: (v) => <b>{formatMoney(v)}</b> },
            { title: "روش", dataIndex: "method", render: (v, it) => (
              <Space size={4}>
                {v === "insurance" ? "بیمه — بانکِ رسمی"
                  : v === "transfer" ? "انتقال به همکار" : (METHODS.find((m) => m.value === v)?.label || v || "—")}
                {it.manual_override && <Tag color="purple" style={{ margin: 0 }}>اصلاحِ دستی</Tag>}
              </Space>) },
            { title: "پیگیری", dataIndex: "reference_number", render: (v) => v || "—" },
            { title: "سند", dataIndex: "doc_no", render: (v) => (v ? <code>{v}</code> : "—") },
            { title: "", render: (_, it) => (edit && it.editable ? (
              <Space size={2}>
                <Button size="small" type="text" icon={<EditOutlined />} title="ویرایش" onClick={() => startItemEdit(it)} />
                <Popconfirm title="این پرداخت حذف شود؟" description={it.auto
                    ? "مبلغش به ماندهٔ پرداخت برمی‌گردد و همگام‌سازیِ بیمه دیگر آن را نمی‌سازد."
                    : "مبلغش به ماندهٔ پرداختِ این نفر برمی‌گردد."}
                  okText="حذف" cancelText="خیر" onConfirm={() => deleteSalaryPayment(it.id)
                    .then(() => { message.success("حذف شد"); loadItems(itemsFor); load(); })
                    .catch((e) => message.error(apiError(e)))}>
                  <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Space>) : (!it.editable ? <Text type="secondary" style={{ fontSize: 11 }}>از «انتقال بین پرسنل»</Text> : null)) },
          ]} />
        {editItem && (
          <Card size="small" style={{ marginTop: 12 }} title={`ویرایشِ پرداختِ ${editItem.doc_no || ""}`}>
            <Form form={editForm} layout="vertical">
              <Space wrap align="start">
                <Form.Item name="amount" label="مبلغ (ریال)" rules={[{ required: true }]}><MoneyInput style={{ width: 180 }} /></Form.Item>
                <Form.Item name="paid_at" label="تاریخ" rules={[{ required: true }]}><JalaliDateInput /></Form.Item>
                <Form.Item name="method" label="روش">
                  {editItem.auto
                    ? <Select disabled style={{ width: 150 }} options={[{ value: "insurance", label: "بیمه — بانکِ رسمی" }]} />
                    : <Select options={METHODS} style={{ width: 120 }} />}
                </Form.Item>
                <Form.Item name="reference_number" label="شمارهٔ پیگیری"><input className="ant-input" style={{ width: 150 }} /></Form.Item>
                <Form.Item name="source" label="مبدأ"><SourceSelect style={{ width: 180 }} /></Form.Item>
              </Space>
              {editItem.auto && (
                <Alert type="info" showIcon style={{ marginBottom: 8 }}
                  message="این پرداخت از فایلِ بانکِ رسمیِ بیمه آمده"
                  description="بعد از ذخیره «اصلاحِ دستی» می‌خورد و ایمپورت/همگام‌سازیِ بعدیِ بیمه دیگر مبلغش را عوض نمی‌کند." />
              )}
              <Space>
                <Button type="primary" onClick={() => editForm.validateFields().then((v) => {
                  const { source, ...rest } = v;
                  return updateSalaryPayment(editItem.id, { ...rest, note: editItem.note, ...(source || OFFICE) })
                    .then(() => { message.success("پرداخت ویرایش شد"); setEditItem(null); loadItems(itemsFor); load(); })
                    .catch((e) => message.error(apiError(e)));
                })}>ذخیره</Button>
                <Button onClick={() => setEditItem(null)}>انصراف</Button>
              </Space>
            </Form>
          </Card>
        )}
      </Modal>

      <AdminPinModal open={pinOpen} title="بازگشاییِ پروندهٔ پرداخت"
        description="بعد از بازگشایی، پرداخت/ویرایش/انتقالِ این ماه دوباره ممکن می‌شود."
        onCancel={() => setPinOpen(false)}
        onConfirm={(pin) => reopenPeriodPayments(periodId, pin)
          .then(() => { message.success("پرونده دوباره باز شد"); setPinOpen(false); load(); })
          .catch((e) => message.error(apiError(e)))} />

      {/* پرداختِ تکی */}
      <Modal open={!!payFor} title={`پرداخت حقوق — ${payFor?.employee_name || ""}`}
        onCancel={() => setPayFor(null)} okText="ثبت پرداخت" cancelText="انصراف"
        onOk={() => form.validateFields().then(async (v) => {
          const { source, ...rest } = v;
          // v2.173.0 — بیش از طلب / صفرِ اضافه / تکراری ⇒ اول ببیند
          if (!(await confirmEntry({ employee_id: payFor.employee_id, amount: v.amount, transaction_type: "salary_paid",
            period_id: periodId, occurred_at: v.paid_at }))) return;
          recordSalaryPayment(periodId, { employee_id: payFor.employee_id, ...rest, ...(source || OFFICE) })
            .then(() => { message.success("پرداخت ثبت شد"); setPayFor(null); load(); })
            .catch((e) => message.error(e?.response?.data?.detail || "ثبت ناموفق بود"));
        })}>
        <Form form={form} layout="vertical">
          <Form.Item name="amount" label="مبلغ (ریال)" rules={[{ required: true }]}>
            <MoneyInput words />
          </Form.Item>
          <Form.Item name="paid_at" label="تاریخ پرداخت" rules={[{ required: true }]}>
            <JalaliDateInput />
          </Form.Item>
          <Form.Item name="method" label="روش پرداخت">
            <Select options={METHODS} />
          </Form.Item>
          <Form.Item name="reference_number" label="شمارهٔ پیگیری">
            <input className="ant-input" />
          </Form.Item>
          <Form.Item name="source" label="مبدأ پرداخت" initialValue={OFFICE}
            extra="تنخواه ⇒ همین پرداخت خودکار در صندوق می‌نشیند">
            <SourceSelect style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* پرداختِ دسته‌جمعی */}
      <Modal open={bulkOpen} title="پرداختِ ماندهٔ همهٔ پرسنل"
        onCancel={() => setBulkOpen(false)} okText="ثبت" cancelText="انصراف"
        onOk={() => bulkForm.validateFields().then((v) => {
          const { source, ...rest } = v;
          // v2.167.0: با فیلترِ لیست (غیرکارمزدی/کارمزدی) فقط همان گروه
          payPeriodSalariesBulk(periodId, { ...rest, ...(source || OFFICE),
            ...(group !== "all" ? { employee_ids: groupRows.filter((r) => r.remaining > 0).map((r) => r.employee_id) } : {}) })
            .then((r) => { message.success(r.data.message); setBulkOpen(false); load(); })
            .catch((e) => message.error(e?.response?.data?.detail || "ثبت ناموفق بود"));
        })}>
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          message={`ماندهٔ ${t?.unpaid_count ?? 0} نفر${group !== "all" ? ` (${GROUP_FA[group]})` : ""} یک‌جا پرداخت‌شده ثبت می‌شود`}
          description="فقط کسانی که مانده دارند. بعداً هر ردیف را می‌شود جدا حذف کرد." />
        <Form form={bulkForm} layout="vertical">
          <Form.Item name="paid_at" label="تاریخ پرداخت" rules={[{ required: true }]}>
            <JalaliDateInput />
          </Form.Item>
          <Form.Item name="method" label="روش پرداخت">
            <Select options={METHODS} />
          </Form.Item>
          <Form.Item name="source" label="مبدأ پرداخت" initialValue={OFFICE}
            extra="پیش‌فرض حسابِ دفتر؛ برای پرداختِ نقدی از صندوق، تنخواه را انتخاب کنید">
            <SourceSelect style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
